import React, { ErrorInfo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import * as Sentry from '@sentry/react';
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    Sentry.captureException(error);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ error }: { error: Error | null }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-background text-foreground">
      <h1 className="text-2xl font-bold mb-4">{t('errorOccurred')}</h1>
      <p className="text-lg mb-4">{t('errorMessage')}</p>
      {error && (
        <pre className="bg-muted p-4 rounded-md mb-4 max-w-full overflow-x-auto">
          {error.toString()}
        </pre>
      )}
      <Button onClick={() => window.location.reload()}>{t('refreshPage')}</Button>
    </div>
  );
}

export default ErrorBoundary;
